import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Box, Alert, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * MuiForm - Form wrapper with Formik integration
 *
 * Jakob's Law: Standard form behavior users expect
 * Hick's Law: Clear validation reduces decision complexity
 * Miller's Law: Groups fields logically to reduce cognitive load
 */

const StyledForm = styled(Form)(({ theme }) => ({
  width: '100%',
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const FormActions = styled(Box)(({ theme, position }) => ({
  display: 'flex',
  justifyContent: position === 'right' ? 'flex-end' : position === 'center' ? 'center' : 'flex-start',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MuiForm = ({
  initialValues,
  validationSchema,
  onSubmit,
  children,
  enableReinitialize = false,
  validateOnMount = false,
  validateOnChange = true,
  validateOnBlur = true,
  showErrorAlert = true,
  errorAlertPosition = 'top',
  actionsPosition = 'right',
  actions,
  spacing = 2,
  formProps = {},
  innerRef,
  ...props
}) => {
  const renderContent = (formikProps) => {
    const { errors, submitCount, isSubmitting, status } = formikProps;

    // Get form-level error message
    const formError = status?.error || (submitCount > 0 && Object.keys(errors).length > 0
      ? 'Please correct the errors below before submitting.'
      : null);

    const errorAlert = showErrorAlert && formError && (
      <Collapse in={!!formError}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => formikProps.setStatus({ ...status, error: null })}
        >
          {formError}
        </Alert>
      </Collapse>
    );

    const content = typeof children === 'function'
      ? children(formikProps)
      : children;

    return (
      <StyledForm {...formProps}>
        {errorAlertPosition === 'top' && errorAlert}
        <Box sx={{ '& > * + *': { mt: spacing } }}>
          {content}
        </Box>
        {errorAlertPosition === 'bottom' && errorAlert}
        {actions && (
          <FormActions position={actionsPosition}>
            {typeof actions === 'function' ? actions(formikProps) : actions}
          </FormActions>
        )}
      </StyledForm>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={enableReinitialize}
      validateOnMount={validateOnMount}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}
      innerRef={innerRef}
      {...props}
    >
      {renderContent}
    </Formik>
  );
};

// Sub-components for structured form layouts
MuiForm.Section = FormSection;
MuiForm.Row = FormRow;
MuiForm.Actions = FormActions;

MuiForm.propTypes = {
  /** Initial form values */
  initialValues: PropTypes.object.isRequired,
  /** Yup validation schema */
  validationSchema: PropTypes.object,
  /** Form submission handler */
  onSubmit: PropTypes.func.isRequired,
  /** Form content - can be a render function receiving formik props */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /** Enable form reinitialization when initialValues change */
  enableReinitialize: PropTypes.bool,
  /** Validate form on mount */
  validateOnMount: PropTypes.bool,
  /** Validate on field change */
  validateOnChange: PropTypes.bool,
  /** Validate on field blur */
  validateOnBlur: PropTypes.bool,
  /** Show error alert for form errors */
  showErrorAlert: PropTypes.bool,
  /** Position of error alert */
  errorAlertPosition: PropTypes.oneOf(['top', 'bottom']),
  /** Position of form actions */
  actionsPosition: PropTypes.oneOf(['left', 'center', 'right']),
  /** Action buttons - can be a render function receiving formik props */
  actions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Spacing between form elements */
  spacing: PropTypes.number,
  /** Additional props for the Form element */
  formProps: PropTypes.object,
  /** Ref to Formik instance */
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default MuiForm;
